/**
 * Formatting helpers.
 *
 * Numbers and dates follow the active locale (Czech uses a decimal comma and a
 * narrow no-break space as the thousands separator), while the stored values
 * stay untouched (§12).
 */

import i18next from '../i18n';

function locale(): string {
  return i18next.language === 'en' ? 'en-GB' : 'cs-CZ';
}

export function formatNumber(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(locale(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/** Watts shown in the unit a human reads: W, kW or MW. */
export function formatPower(watts: number | null | undefined): string {
  if (watts === null || watts === undefined) return '—';
  const absolute = Math.abs(watts);
  if (absolute >= 1_000_000) return `${formatNumber(watts / 1_000_000, 2)} MW`;
  if (absolute >= 1_000) return `${formatNumber(watts / 1_000, 1)} kW`;
  return `${formatNumber(watts, 0)} W`;
}

export function formatEnergy(wattHours: number | null | undefined): string {
  if (wattHours === null || wattHours === undefined) return '—';
  const absolute = Math.abs(wattHours);
  if (absolute >= 1_000_000_000) return `${formatNumber(wattHours / 1_000_000_000, 2)} GWh`;
  if (absolute >= 1_000_000) return `${formatNumber(wattHours / 1_000_000, 2)} MWh`;
  if (absolute >= 1_000) return `${formatNumber(wattHours / 1_000, 1)} kWh`;
  return `${formatNumber(wattHours, 0)} Wh`;
}

export function formatCapacity(wattPeak: number | null | undefined): string {
  if (wattPeak === null || wattPeak === undefined) return '—';
  if (wattPeak >= 1_000_000) return `${formatNumber(wattPeak / 1_000_000, 2)} MWp`;
  return `${formatNumber(wattPeak / 1_000, 1)} kWp`;
}

export function formatPercent(ratio: number | null | undefined, digits = 0): string {
  if (ratio === null || ratio === undefined) return '—';
  return `${formatNumber(ratio * 100, digits)} %`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(locale(), {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(new Date(iso));
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(locale(), { timeStyle: 'short' }).format(new Date(iso));
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(locale(), { dateStyle: 'medium' }).format(new Date(iso));
}

/** Human-readable age, used next to every live value (§1.4). */
export function describeAge(seconds: number | null | undefined): {
  key: string;
  value: number;
} | null {
  if (seconds === null || seconds === undefined) return null;
  if (seconds < 120) return { key: 'freshness.ageSeconds', value: Math.round(seconds) };
  if (seconds < 7200) return { key: 'freshness.ageMinutes', value: Math.round(seconds / 60) };
  return { key: 'freshness.ageHours', value: Math.round(seconds / 3600) };
}

export function isoDateInput(date: Date): string {
  return date.toISOString().slice(0, 10);
}
